import React from "react";
import { Field, ErrorMessage } from "formik";
import Checkbox from "src/components/Forms/FormikCheckbox";
import FormikSelect from "src/components/Forms/FormikSelect";
import { contactInfoType } from "ps-shared";
import FormikMultiSelect, { SelectOption } from "src/components/Forms/FormikMultiSelect";
import { FormikMultiCreateSelect } from "src/components/Forms/FormikMultiCreateSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { FormGroup, Label, UncontrolledTooltip } from "reactstrap";
import languageList from "ps-shared/models/forms/languages.json";
import categoriesList from "ps-shared/models/forms/categories.json";
import postNominalsList from "ps-shared/models/forms/postNominals.json";
import race from "ps-shared/models/forms/race.json";
import CheckboxGroup from "src/components/Forms/FormikCheckboxGroup";
// tslint:disable-next-line:no-empty-interface
export interface ContactInfoFormCb {}

interface ContactInfoProps {
  values: contactInfoType;
}
interface ContactInfoState {
  languageOptions: SelectOption[];
  categories: SelectOption[];
  postNominals: SelectOption[];
  races: SelectOption[];
}

export class ContactInfoForm extends React.Component<ContactInfoProps, ContactInfoState> {
  constructor(props: ContactInfoProps) {
    super(props);
    this.state = {
      languageOptions: languageList.map((language: any) => {
        const opt = {} as SelectOption;
        opt.label = language.name;
        opt.value = language.code;
        return opt;
      }),
      categories: categoriesList.map((title: string) => {
        return {
          label: title,
          value: title,
        } as SelectOption;
      }),
      postNominals: postNominalsList.map((pn: string) => {
        return {
          label: pn,
          value: pn,
        } as SelectOption;
      }),
      races: race.map((e: string) => {
        return {
          label: e,
          value: e,
        } as SelectOption;
      }),
    };
  }

  // tslint:disable:max-line-length
  public render() {
    return (
      <>
        <FormGroup className="mb-3">
          <Label for="firstName">First Name:</Label>
          <br />
          <Field placeholder="First Name" type="text" component="input" name="firstName" className="form-control" />
          <ErrorMessage name="firstName" component="div" className="position-relative form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="lastName">Last Name:</Label>
          <br />
          <Field placeholder="Last Name" type="text" name="lastName" component="input" className="form-control" />
          <ErrorMessage name="lastName" component="div" className="position-relative form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="postNominal">Post Nominal Letter(s):</Label>
          <br />
          <FormikMultiCreateSelect
            options={this.state.postNominals}
            placeholder="Select your post nominal letters or type to enter"
            value={this.props.values.postNominal}
            name="postNominal"
            noOptionsMessage="Add your post nominal letter(s) eg BSc or PHd or MD - each post nominal limited to 8 characters"
            validationFunc={(value: string): boolean => {
              // eslint-disable-next-line no-useless-escape
              if (value.length >= 1 && value.length <= 13 && value.match(/^[A-Za-z\-]+$/)) {
                return true;
              }
              return false;
            }}
          />
          <ErrorMessage name="postNominal" component="div" className="form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <UncontrolledTooltip delay={0} placement="right" target="titleTT">
            For example: Licensed Mental Health Counselor, Psychiatrist, Licensed Addiction Counselor
          </UncontrolledTooltip>
          <Label for="title">Professional Title:</Label>&nbsp;
          <span id="titleTT" style={{ fontSize: "10px" }}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </span>
          <br />
          <Field placeholder="Professional title" type="text" name="title" component="input" className="form-control" />
          <ErrorMessage name="title" component="div" className="form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <UncontrolledTooltip delay={0} placement="right" target="professionTT">
            Select the closest professional title matching your practice. This term appears in the website filter.
          </UncontrolledTooltip>
          <Label for="title">Profession:</Label>&nbsp;
          <span id="professionTT" style={{ fontSize: "10px" }}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </span>
          <br />
          <FormikSelect options={this.state.categories} value={this.props.values.profession} name="profession" />
          <ErrorMessage name="profession" component="div" className="form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="phone">Business Phone:</Label>
          <br />
          <Field placeholder="Phone" type="text" name="phone" component="input" className="form-control" />
          <ErrorMessage name="phone" component="div" className="position-relative form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="email">
            Email: <i>(hidden from public)</i>
          </Label>
          <br />
          <Field placeholder="Email" type="text" name="email" component="input" className="form-control" />
          <ErrorMessage name="email" component="div" className="position-relative form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="preferredContact">Preferred Contact:</Label>
          <br />
          <CheckboxGroup name="preferredContact" value="email" label="Email" />
          <CheckboxGroup name="preferredContact" value="phone" label="Phone" />
          <ErrorMessage name="preferredContact" component="div" className="form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <UncontrolledTooltip delay={0} placement="right" target="websiteTT">
            Copy/paste the url here. It requires the full link from your browser including http:// or https://
          </UncontrolledTooltip>
          <Label for="web">Website(s):</Label>&nbsp;
          <span id="websiteTT" style={{ fontSize: "10px" }}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </span>
          <br />
          <FormikMultiCreateSelect
            options={[]}
            value={this.props.values.web}
            name="web"
            noOptionsMessage="Add your website(s)"
            validationFunc={(value: string): boolean => {
              try {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const url = new URL(value);
                return true;
              } catch {
                return false;
              }
            }}
            customErr="Invalid URL, please be sure to prefix with the protocol eg http:// or https://"
          />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="languages">Languages Spoken:</Label>
          <br />
          <FormikMultiSelect
            options={this.state.languageOptions}
            value={this.props.values.languages}
            name="languages"
          />
        </FormGroup>
        <FormGroup className="mb-3">
          <UncontrolledTooltip delay={0} placement="right" target="raceTT">
            Select your race from the list or type to enter. Multiple race can appear on your profile.
          </UncontrolledTooltip>
          <Label for="web">Provider Race:</Label>&nbsp;
          <span id="raceTT" style={{ fontSize: "10px" }}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </span>
          <br />
          <FormikMultiCreateSelect
            options={this.state.races}
            value={this.props.values.race}
            name="race"
            noOptionsMessage="Add your race"
          />
          <ErrorMessage name="race" component="div" className="form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="codeOfConduct">Terms of Service:</Label>
          <br />
          <Checkbox
            name="codeOfConduct"
            value={true}
            label="I agree to the <a href='/terms-of-service/' target='_blank'/>Terms of Service</a>"
          />
          <ErrorMessage name="codeOfConduct" component="div" className="form-feedback" />
        </FormGroup>
      </>
    );
  }
}
